import { z } from 'zod';
import { clientProfileSchema } from './client-profile';
/**
 * Минимальные требования к паролю.
 *
 * Слабее, чем хотелось бы для regulated workload, но достаточно для MVP:
 * - 8+ символов
 * - хотя бы одна буква и одна цифра
 *
 * Усилим (с инвалидацией старых паролей и принудительной сменой) при необходимости.
 */
export const passwordSchema = z
    .string()
    .min(8, 'Пароль должен быть не короче 8 символов')
    .max(256, 'Пароль слишком длинный')
    .regex(/[A-Za-zА-Яа-я]/, 'Пароль должен содержать хотя бы одну букву')
    .regex(/\d/, 'Пароль должен содержать хотя бы одну цифру');
/**
 * Регистрация клиента. Только для роли CLIENT (по плану этапа 2).
 *
 * Профиль передаётся discriminated union'ом по `profile.type`.
 * После успешной регистрации создаются: User (role=CLIENT, emailVerified=false),
 * Client (со связанной type-specific таблицей), EmailVerification (отправляется в логи).
 */
export const registerClientSchema = z.object({
    email: z.string().email(),
    password: passwordSchema,
    displayName: z.string().min(1).max(200),
    phone: z.string().max(50).optional(),
    profile: clientProfileSchema,
});
/** Ответ регистрации — без чувствительных данных. */
export const registerClientResponseSchema = z.object({
    userId: z.string(),
    clientId: z.string(),
    serviceId: z.string(),
    email: z.string().email(),
    emailVerified: z.boolean(),
    /** Только в dev-режиме — токен и ссылка для верификации (в production — null). */
    devVerification: z
        .object({
        token: z.string(),
        url: z.string(),
    })
        .nullable(),
});
/**
 * Демо-стенд: самостоятельная регистрация сотрудника-экспедитора (MANAGER).
 * Доступно только при DEMO_MODE. Реальный email (подтверждение как у клиента).
 */
export const demoRegisterExpeditorSchema = z.object({
    email: z.string().email(),
    password: passwordSchema,
    fullName: z.string().min(1).max(200),
    phone: z.string().max(50).optional(),
});
/** Запрос на вход. */
export const loginSchema = z.object({
    email: z.string().email(),
    password: z.string().min(1),
});
/** Пара токенов. */
export const tokenPairSchema = z.object({
    accessToken: z.string(),
    refreshToken: z.string(),
    /** Время до истечения access в секундах. */
    expiresIn: z.number().int().positive(),
    tokenType: z.literal('Bearer'),
});
/** Запрос на обновление токена. */
export const refreshSchema = z.object({
    refreshToken: z.string().min(1),
});
/** Подтверждение email — токен передаётся в query string. */
export const verifyEmailSchema = z.object({
    token: z.string().min(1),
});
/** Смена пароля — требует знания текущего. */
export const changePasswordSchema = z.object({
    currentPassword: z.string().min(1),
    newPassword: passwordSchema,
});
/** Стандартное «кто я» — для текущего пользователя на фронте. */
export const meResponseSchema = z.object({
    userId: z.string(),
    email: z.string().email(),
    role: z.enum(['CLIENT', 'MANAGER', 'ADMIN', 'AUDITOR', 'OVERSIGHT']),
    emailVerified: z.boolean(),
    clientId: z.string().nullable(),
    displayName: z.string().nullable(),
    /** MANAGER с правом действовать от имени клиента (иначе false). */
    canActForClients: z.boolean().default(false),
});
//# sourceMappingURL=auth.js.map